import type { System } from '../core/Engine';
import { dynamicResolution } from './createRenderer';

export interface DynamicResolutionOptions {
  /** Frame time (ms) that counts as too slow. */
  slowMs: number;
  /** True when frames are worth measuring (after boot, not during loading hitches). */
  canMeasure: () => boolean;
  /** Called with the new scale after each change. */
  onChange?: (scale: number) => void;
}

/**
 * Dynamic resolution: on slower GPUs, trade render scale for frame rate, and give
 * it back when there's headroom. Frame intervals are averaged over a couple of
 * seconds; above `slowMs` the scale steps down (to 55% at most). If a step
 * doesn't help (CPU-bound, or a capped display) it is undone and adjustment stops.
 */
export class DynamicResolution implements System {
  readonly name = 'dynres';
  private sum = 0;
  private n = 0;
  private last = performance.now();
  private changed = performance.now();
  private lastAvg = 0;
  private stuck = false;

  constructor(private readonly opts: DynamicResolutionOptions) {}

  frameUpdate(): void {
    const now = performance.now();
    const dt = now - this.last;
    this.last = now;
    // Background tabs and hitches (dt > 250 ms) say nothing about steady-state speed.
    if (!this.opts.canMeasure() || dt > 250) return;
    this.sum += dt;
    this.n++;
    if (now - this.changed < 2500 || this.n < 45) return;
    const avg = this.sum / this.n;
    this.sum = 0;
    this.n = 0;
    const s = dynamicResolution.scale;
    let next = s;
    if (avg > this.opts.slowMs && !this.stuck) {
      if (this.lastAvg > 0 && avg > this.lastAvg * 0.92) {
        next = Math.min(1, s / 0.85);
        this.stuck = true;
      } else {
        next = Math.max(0.55, s * 0.85);
      }
    } else if (avg < 17.5 && s < 1) {
      next = Math.min(1, s * 1.1);
    }
    if (next !== s) {
      dynamicResolution.scale = next;
      this.changed = now;
      this.lastAvg = next < s ? avg : 0;
      this.opts.onChange?.(next);
    }
  }
}
